/**
 * 8,760-hour behind-the-meter battery dispatch, self-consumption only
 * (DESIGN §4.3). Ported from ES `src/billing/battery-dispatch.ts`, keeping:
 * - charge only from solar surplus (no grid charging: flat rate, no arbitrage);
 * - discharge only to cover the home deficit, never exporting;
 * - power limit, SOC clamped to [minSoc, maxSoc], √RTE symmetric efficiency;
 * - daily discharge cap = usable capacity × maxDailyCycles;
 * - mutual exclusivity (no discharge in an hour with any charge).
 * TOU-arbitrage and grid-charging branches are dropped. Min SOC is the VPP
 * backup reserve (default 20% of capacity, so DoD is 80%).
 *
 * The `load` passed in must be the HOME load only. The battery never
 * discharges into EV charging.
 *
 * Pure: no DOM, network or global state.
 */
import { HOURS_IN_YEAR } from "./calendar";

export interface BatteryConfig {
  /** Nameplate capacity, kWh. 0 disables the battery. */
  readonly capacityKwh: number;
  /** Max charge/discharge power, kW (= kWh per hour). */
  readonly powerKw: number;
  /** Round-trip efficiency, e.g. 0.90. Split as √RTE each way. */
  readonly roundTripEfficiency: number;
  /** Reserve held for backup / VPP events, fraction of capacity (min SOC). */
  readonly reservePct: number;
  /** Max full usable-capacity discharges per day. */
  readonly maxDailyCycles: number;
}

export interface BatteryHourly {
  /** Energy drawn from solar surplus into the battery (AC side), kWh. */
  readonly charge: Float64Array;
  /** Energy delivered to the home (AC side), kWh. */
  readonly discharge: Float64Array;
  /** State of charge at the end of each hour, kWh. */
  readonly soc: Float64Array;
  /** `load − solar − (discharge − charge)`: >0 import, <0 export, kWh. */
  readonly netGrid: Float64Array;
}

export interface BatterySummary {
  readonly annualChargedKwh: number;
  readonly annualDischargedKwh: number;
  readonly annualRoundTripLossKwh: number;
  readonly equivalentCycles: number;
  readonly peakChargeKw: number;
  readonly peakDischargeKw: number;
  readonly minSocKwh: number;
  readonly maxSocKwh: number;
}

export interface BatteryResult {
  readonly hourly: BatteryHourly;
  readonly summary: BatterySummary;
}

function validateVector(v: ArrayLike<number>, name: string): void {
  if (v.length !== HOURS_IN_YEAR) throw new Error(`Invalid ${name} length ${v.length}; expected ${HOURS_IN_YEAR}`);
}

/** Simulates self-consumption dispatch against the home load. */
export function simulateBattery(
  load: ArrayLike<number>,
  solar: ArrayLike<number>,
  cfg: BatteryConfig,
): BatteryResult {
  validateVector(load, "load");
  validateVector(solar, "solar");
  const charge = new Float64Array(HOURS_IN_YEAR);
  const discharge = new Float64Array(HOURS_IN_YEAR);
  const soc = new Float64Array(HOURS_IN_YEAR);
  const netGrid = new Float64Array(HOURS_IN_YEAR);

  const capacity = Math.max(0, cfg.capacityKwh);
  const power = Math.max(0, cfg.powerKw);
  if (capacity <= 0 || power <= 0) {
    for (let h = 0; h < HOURS_IN_YEAR; h++) netGrid[h] = load[h] - solar[h];
    return {
      hourly: { charge, discharge, soc, netGrid },
      summary: {
        annualChargedKwh: 0,
        annualDischargedKwh: 0,
        annualRoundTripLossKwh: 0,
        equivalentCycles: 0,
        peakChargeKw: 0,
        peakDischargeKw: 0,
        minSocKwh: 0,
        maxSocKwh: 0,
      },
    };
  }
  if (!(cfg.roundTripEfficiency > 0 && cfg.roundTripEfficiency <= 1)) {
    throw new Error(`roundTripEfficiency must be in (0, 1], got ${cfg.roundTripEfficiency}`);
  }
  if (!(cfg.reservePct >= 0 && cfg.reservePct < 1)) {
    throw new Error(`reservePct must be in [0, 1), got ${cfg.reservePct}`);
  }

  const minSoc = capacity * cfg.reservePct;
  const maxSoc = capacity;
  const eta = Math.sqrt(cfg.roundTripEfficiency);
  const dailyCap = (maxSoc - minSoc) * Math.max(0, cfg.maxDailyCycles);

  let cur = minSoc;
  let charged = 0;
  let discharged = 0;
  let peakC = 0;
  let peakD = 0;

  for (let d = 0; d < 365; d++) {
    let dayDischarged = 0;
    for (let hod = 0; hod < 24; hod++) {
      const h = d * 24 + hod;
      const l = load[h];
      const s = solar[h];
      const surplus = s > l ? s - l : 0;
      const deficit = l > s ? l - s : 0;
      let c = 0;
      let dis = 0;

      if (surplus > 0) {
        c = Math.min(surplus, power, Math.max(0, (maxSoc - cur) / eta));
        if (c > 0) cur = Math.min(maxSoc, cur + c * eta);
      }
      if (c === 0 && deficit > 0) {
        dis = Math.min(deficit, power, Math.max(0, (cur - minSoc) * eta), Math.max(0, dailyCap - dayDischarged));
        if (dis > 0) {
          cur = Math.max(minSoc, cur - dis / eta);
          dayDischarged += dis;
        }
      }

      charge[h] = c;
      discharge[h] = dis;
      soc[h] = cur;
      netGrid[h] = l - s - (dis - c);
      charged += c;
      discharged += dis;
      if (c > peakC) peakC = c;
      if (dis > peakD) peakD = dis;
    }
  }

  return {
    hourly: { charge, discharge, soc, netGrid },
    summary: {
      annualChargedKwh: charged,
      annualDischargedKwh: discharged,
      annualRoundTripLossKwh: Math.max(0, charged - discharged),
      equivalentCycles: maxSoc - minSoc > 0 ? discharged / (maxSoc - minSoc) : 0,
      peakChargeKw: peakC,
      peakDischargeKw: peakD,
      minSocKwh: minSoc,
      maxSocKwh: maxSoc,
    },
  };
}
